/**
 * SpecialtySignalsStrip
 * ──────────────────────────────────────────────────
 * Top sub-section inside the Clinical Suggestions tab.
 * Renders specialty-tuned "Clinical Signals" pulled from
 * `specialtyConfig.signals` — this is the surface that
 * changes instantly when the demo switcher flips specialty.
 */
import { AlertCircle, ShieldCheck, Sparkles, Info } from 'lucide-react';
import { useSpecialty } from '../../../contexts/SpecialtyContext';
import { toast } from 'sonner';

const TONE = {
  amber:  { cls: 'border-amber-100 bg-amber-50/40',  chip: 'bg-amber-100 text-amber-800',  icon: AlertCircle },
  green:  { cls: 'border-emerald-100 bg-emerald-50/30', chip: 'bg-emerald-100 text-emerald-800', icon: ShieldCheck },
  purple: { cls: 'border-violet-100 bg-violet-50/30', chip: 'bg-violet-100 text-violet-800', icon: Sparkles },
  blue:   { cls: 'border-blue-100 bg-blue-50/30',    chip: 'bg-blue-100 text-blue-800',     icon: Info },
};

export const SpecialtySignalsStrip = () => {
  const { specialty } = useSpecialty();
  const allSignals = specialty.signals || [];
  /* Filter by Settings toggles. Each signal maps to a class via its tone
     (this is a mock mapping: amber→compliance, green→care_gaps,
     purple→coding, blue→context). */
  let enabled = { care_gaps: true, coding: true, compliance: true, context: true };
  try {
    const raw = localStorage.getItem('lumina_cp_signals');
    if (raw) enabled = { ...enabled, ...JSON.parse(raw) };
  } catch (_) {}
  const toneToClass = { amber: 'compliance', green: 'care_gaps', purple: 'coding', blue: 'context' };
  const signals = allSignals.filter((s) => enabled[toneToClass[s.tone] || 'context'] !== false);

  return (
    <div className="space-y-2 mb-3 pb-3 border-b border-slate-100" data-testid="specialty-signals-strip">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-slate-700" />
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          {specialty.label} · Clinical Signals
        </p>
        <span className="ml-auto text-[9px] text-slate-400">Specialty-tuned</span>
      </div>
      {signals.length === 0 ? (
        <p className="text-[10px] text-slate-400 italic">No specialty-specific signals configured.</p>
      ) : signals.map((sig, i) => {
        const t = TONE[sig.tone] || TONE.blue;
        const Icon = t.icon;
        return (
          <div
            key={i}
            className={`flex items-start gap-2 p-2 rounded-lg border ${t.cls}`}
            data-testid={`specialty-signal-${i}`}
          >
            <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full ${t.chip} shrink-0`}>
              <Icon className="h-2.5 w-2.5" />
            </span>
            <p className="flex-1 text-[11px] text-slate-700 leading-snug">{sig.text}</p>
            <button
              type="button"
              onClick={() => toast.success('Signal added to note')}
              className="text-[9px] font-semibold text-slate-600 hover:text-slate-900 bg-white px-1.5 py-0.5 rounded border border-slate-200"
              data-testid={`specialty-signal-add-${i}`}
            >
              + Add
            </button>
          </div>
        );
      })}
      {specialty.example_phrases?.length > 0 && (
        <div className="mt-1 px-2 py-1.5 rounded bg-slate-50 border border-slate-100">
          <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Vocab cues</p>
          <p className="text-[10px] text-slate-600 italic">"{specialty.example_phrases[0]}"</p>
        </div>
      )}
    </div>
  );
};

export default SpecialtySignalsStrip;
